import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(
        res.data?.message ||
          "Thanks! We've received your message and will get back to you soon.",
      );
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(
        Array.isArray(detail) ? "Please check your inputs." : detail,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="bg-white"
      data-testid="contact-section"
    >
      <div className="container-kw section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow">Get in Touch</div>
            <h2 className="mt-5 font-display font-bold text-4xl md:text-5xl tracking-tight text-[color:var(--kw-black)]">
              Let&apos;s talk.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[color:var(--kw-muted)] leading-relaxed max-w-md">
              Partnerships, product feedback, or just a hello — we read every
              message.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center"
                  style={{ background: "var(--kw-yellow)" }}
                >
                  <Mail className="h-4 w-4" />
                </div>
                <div>
                  <div className="eyebrow">Email</div>
                  <a
                    href="mailto:team@kingswake.co.in"
                    className="mt-1 block font-medium text-[color:var(--kw-black)] hover:text-[color:var(--kw-muted)] transition-colors"
                    data-testid="contact-email-link"
                  >
                    team@kingswake.co.in
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center border"
                  style={{ borderColor: "var(--kw-border)" }}
                >
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <div className="eyebrow">Location</div>
                  <div className="mt-1 font-medium text-[color:var(--kw-black)]">
                    Punjab, India — 141412
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl border p-6 md:p-10"
              style={{ borderColor: "var(--kw-border)" }}
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="eyebrow" htmlFor="contact-name">
                    Name
                  </label>
                  <Input
                    id="contact-name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={update("name")}
                    className="mt-2 h-12 rounded-xl"
                    data-testid="contact-input-name"
                    required
                  />
                </div>
                <div>
                  <label className="eyebrow" htmlFor="contact-email">
                    Email
                  </label>
                  <Input
                    id="contact-email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={update("email")}
                    className="mt-2 h-12 rounded-xl"
                    data-testid="contact-input-email"
                    required
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="eyebrow" htmlFor="contact-message">
                  Message
                </label>
                <Textarea
                  id="contact-message"
                  placeholder="Tell us a bit about what you have in mind…"
                  value={form.message}
                  onChange={update("message")}
                  rows={6}
                  className="mt-2 rounded-xl resize-none"
                  data-testid="contact-input-message"
                  required
                />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-[color:var(--kw-muted)] max-w-sm">
                  By submitting, you agree to be contacted by King&apos;s Wake
                  Digital regarding your enquiry.
                </p>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-pill btn-primary h-12 px-7 disabled:opacity-70"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
